import { useState } from 'react';
import { IconClose } from '@components/icons';
import { IconButton } from '@components/IconButton';
import { Input } from '@components/Input';
import { Button } from '@components/Button';
import { SocialIcon } from '@components/SocialIcon';
import styles from './AddWhatsappModal.module.scss';

export interface AddWhatsappModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Close handler */
  onClose: () => void;

  /** Called when user clicks Save with a valid number */
  onSave?: (whatsappNumber: string) => void;

  /** Pre-filled number for edit mode */
  defaultNumber?: string;
}

const PHONE_REGEX = /^\+?[0-9\s\-()]{7,20}$/;

/**
 * Inner form component that mounts/unmounts with the modal,
 * ensuring state resets naturally on each open.
 */
const AddWhatsappForm = ({
  onClose,
  onSave,
  defaultNumber,
}: Pick<AddWhatsappModalProps, 'onClose' | 'onSave' | 'defaultNumber'>) => {
  const [number, setNumber] = useState(defaultNumber || '');

  const isEditing = !!defaultNumber;
  const isNumberValid = PHONE_REGEX.test(number.trim());

  const handleSave = () => {
    if (!isNumberValid) return;
    onSave?.(number.trim());
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && isNumberValid) {
      handleSave();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
      role="presentation"
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog needs event handlers */}
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-whatsapp-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          e.stopPropagation();
          handleKeyDown(e);
        }}
      >
        {/* Header */}
        <div className={styles.header}>
          <h2 id="add-whatsapp-title" className={styles.title}>
            {isEditing ? 'Edit WhatsApp Number' : 'Add WhatsApp Number'}
          </h2>
          <IconButton
            size="small"
            aria-label="Close modal"
            onClick={onClose}
            className={styles.closeButton}
          >
            <IconClose size={18} />
          </IconButton>
        </div>

        {/* Content */}
        <div className={styles.content}>
          <div className={styles.field}>
            <span className={styles.fieldLabel}>WhatsApp Number</span>
            <Input
              value={number}
              onChange={setNumber}
              placeholder="e.g. +971558703836"
              clearable
              leadingIcon={<SocialIcon provider="whatsapp" className={styles.inputIcon} />}
              className={styles.input}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!isNumberValid}>
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AddWhatsappModal = ({
  isOpen,
  onClose,
  onSave,
  defaultNumber,
}: AddWhatsappModalProps) => {
  if (!isOpen) return null;
  return <AddWhatsappForm onClose={onClose} onSave={onSave} defaultNumber={defaultNumber} />;
};
